export default abstract class NavigationTree {

  private _id: number;
  protected _level: number;
  private _parentId = 0;
  protected _isOpen = false;
  protected _selected = false;
  private _name = '';
  private _nameLocalized = '';
  protected _children: Array<NavigationTree> = [];
  private _parent: NavigationTree | undefined;
 
  constructor(id: number, level: number, parentId: number, parentTree: NavigationTree | undefined) {
    this._level = level;
    this._id = id;
    this._parentId = parentId;
    this._parent = parentTree;
  }

  public getHashMap(): Map<number, NavigationTree> {
    const hashMap = new Map<number, NavigationTree>();
    this._fillHashMap(hashMap);
    return hashMap;
  }

  private _fillHashMap(hashMap: Map<number, NavigationTree>): void {
    hashMap.set(this.id, this);
    for(const childNavTree of this.children) {
      childNavTree._fillHashMap(hashMap);
    }
  }

  public hasNavTree(id: number): boolean {
    if(this.id === id) return true;
    for(const childNavTree of this.children) {
      if(childNavTree.hasNavTree(id)) {
        return true;
      }
    }
    return false;
  }

  public getNavTree(id: number): NavigationTree {
    const uiChannelTree = this._getNavTree(id);
    if(uiChannelTree) return uiChannelTree;
    throw new Error(
      `No NavigationTree instance with id ${id} found ` +
      `in NavigationTree instance with id ${this.id}.`)
  }

  protected _getNavTree(id: number): NavigationTree | undefined {
    if(this.id === id) return this;
    let navTree: NavigationTree | undefined = undefined;
    for(const chilNavTree of this.children) {
      navTree = chilNavTree._getNavTree(id);
      if(navTree) break;
    }
    return navTree;
  }

  public close() {
    //if(this.level === 0) return;
    for(const childNavTree of this.children) {
      childNavTree.close();
    }
    this.isOpen = false;
  }


  public closeOneLevel(): boolean {
    if(!this.isOpen) return false;
    for(const childNavTree of this.children) {
      if(childNavTree.closeOneLevel()) {
        return true;
      }
    }
    this.isOpen = false;
    return true;
  }

  public closeById(id: number) {
    if(!this.hasNavTree(id)) return;
    const navTree = this.getNavTree(id);
    navTree.closeAllChildren();
    navTree.isOpen = false;
  }

  public open() {

    let tree = this.parent as NavigationTree;
    while(tree) {
      tree.isOpen = true;
      tree = tree.parent as NavigationTree;
    }
    this.isOpen = true;
  }

  public openById(id: number) {
    if(!this.hasNavTree(id)) return;
    this.close();
    const navTree = this.getNavTree(id);
    navTree.open();
  }

  public toggle(id: number) {
    if(!this.hasNavTree(id)) return;

    const navTree = this.getNavTree(id);

    if(navTree.isOpen) {
      navTree.close();
    }
    else {
      this.closeAllChildren();
      navTree.open();
    }
  }

  public getOpenTree(): NavigationTree|undefined  {
    const openTree = this._getOpenTree();
    return openTree;
  }

  private _getOpenTree(): NavigationTree|undefined  {
    for(const childNavTree of this.children) {
      if(childNavTree.isOpen) {
        const openChildTree = childNavTree.getOpenTree();
        if(openChildTree) return openChildTree;
        return childNavTree;
      }
    }
    return this.isOpen ? this : undefined;
  }


  public closeChildren() {
    for(const childNavTree of this.children) {
      childNavTree.isOpen = false;
    }
  }
  public closeAllChildren() {
    //if(this.level === 0) return;
    for(const childNavTree of this.children) {
      childNavTree.close();
    }
  }

  public isChildSelected() {
    for(const childNavTree of this.children) {
      if(childNavTree.selected) return true;
      if(childNavTree.selected || childNavTree.isChildSelected()) {
        return true;
      }
    }
    return false;
  }

  public selectUniqueIf(id?: number) {
    if(!id || !this.hasNavTree(id)) return;
    const navTree = this.getNavTree(id);
    if(navTree.selected) return;
    this.deselectAll();
    navTree.selected = true;
  }

  public select(id?: number) {
    if(!id) {
      this.selected = true;
      return;
    }
    if(!this.hasNavTree(id)) return;
    const uiChannelTree = this.getNavTree(id);
    uiChannelTree.select();
  }

  public deselectAll() {
    for(const childNavTree of this.children) {
      childNavTree.deselectAll();
    }
    this.selected = false;
  }

  public hasParent(): boolean {
    return this.parentId !== 0 && this.parent !== undefined;
  }

  public isAncestor(id: number): boolean {
    if(!this.hasParent()) return false;
    if(this.parentId === id) return true;
    const parent = this.parent as NavigationTree;
    return parent.isAncestor(id);
  }

  public hasChildren(): boolean {
    return this.children.length > 0;
  }

  public get id() {
    return this._id;
  }
  public set id(value) {
    this._id = value;
  }
  public get isOpen() {
    return this._isOpen;
  }
  public set isOpen(value) {
    this._isOpen = value;
  }
  public get children(): Array<NavigationTree> {
    return this._children;
  }
  public get level(): number {
    return this._level;
  }
  public set level(value: number) {
    this._level = value;
  }
  public get selected() {
    return this._selected;
  }
  public set selected(value) {
    this._selected = value;
  }
  public get name() {
    return this._name;
  }
  public set name(value) {
    this._name = value;
  }
  public get nameLocalized() {
    return this._nameLocalized;
  }
  public set nameLocalized(value) {
    this._nameLocalized = value;
  }
  public get parentId() {
    return this._parentId;
  }
  public set parentId(value) {
    this._parentId = value;
  }
  public get parent(): NavigationTree | undefined {
    return this._parent;
  }
}